import { loadCourseState, saveCourseState } from '../../actions/learningCourseActions';
import { getUserDetails } from '../../actions/userActions';
import { getAccessibleCourseByCode, getCatalogueCourseByCode } from '../../utils/learningCourseUtils';
import PromoBox from '../includes/PromoBox';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router';

const getChromeNodes = () =>
  [
    document.getElementById('header-container'),
    document.querySelector('footer'),
    document.querySelector('.navigation-secondary'),
    document.querySelector('.page-title'),
  ].filter(Boolean);

const setChromeHidden = (chrome, isHidden) => {
  chrome.forEach((node) => {
    node.hidden = isHidden;
    node.inert = isHidden;
  });
};

const setPageScrollLocked = (isLocked) => {
  document.documentElement.style.overflow = isLocked ? 'hidden' : '';
  document.documentElement.style.overscrollBehavior = isLocked ? 'none' : '';
  document.body.style.overflow = isLocked ? 'hidden' : '';
  document.body.style.overscrollBehavior = isLocked ? 'none' : '';
  document.body.style.position = isLocked ? 'fixed' : '';
  document.body.style.inset = isLocked ? '0' : '';
  document.body.style.width = isLocked ? '100%' : '';
};

const LearningCourse = () => {
  const { courseCode } = useParams();
  const iframeRef = useRef(null);
  const dispatch = useDispatch();
  const userDetails = useSelector((state) => state.user.details);
  const sessionId = useSelector((state) => state.user.sessionId);
  const catalogue = useSelector((state) => state.config.content?.learningCourses);
  const catalogueCourse = getCatalogueCourseByCode(catalogue, courseCode);
  const course = getAccessibleCourseByCode(userDetails, catalogue, courseCode);
  const scormSrc = course?.path && course?.launch ? `/learning-course-content/${course.path}/${course.launch}` : null;
  const [courseError, setCourseError] = useState(null);
  const courseKey = `${scormSrc ?? ''}:${course?.code ?? ''}`;
  const [errorCourseKey, setErrorCourseKey] = useState(courseKey);

  if (errorCourseKey !== courseKey) {
    setErrorCourseKey(courseKey);
    setCourseError(null);
  }

  useEffect(() => {
    if (!scormSrc || !course?.code || !iframeRef.current) {
      return undefined;
    }

    let cancelled = false;
    const chrome = getChromeNodes();
    setChromeHidden(chrome, true);
    setPageScrollLocked(true);

    const data = {};
    const saved = {};
    const dirty = new Set();
    let saveQueue = Promise.resolve();

    const failCourse = (message) => {
      if (cancelled) {
        return;
      }
      setChromeHidden(chrome, false);
      setPageScrollLocked(false);
      delete window.API_1484_11;
      if (iframeRef.current) {
        iframeRef.current.removeAttribute('src');
      }
      setCourseError(message || 'Something went wrong with this course.');
    };

    const applySaveSideEffects = (cmiName, cmiValue) => {
      if (cmiName === 'cmi.completion_status' && cmiValue === 'completed') {
        // Odoo owns completion state; refetch so React + PHP session stay in sync.
        dispatch(getUserDetails(sessionId));
        window.practitionerPortalCourseCompleted?.(course.code);
      }
    };

    const flushDirty = () => {
      saveQueue = saveQueue.then(async () => {
        while (dirty.size > 0) {
          const name = dirty.values().next().value;
          const value = String(data[name] ?? '');
          try {
            const out = await saveCourseState(course.code, name, value);
            if (out?.status === 200) {
              saved[name] = value;
              dirty.delete(name);
              applySaveSideEffects(name, value);
            } else {
              failCourse(`Unable to save course progress. ${out?.message || ''}`.trim());
              break;
            }
          } catch (err) {
            console.error('SCORM save failed', err);
            failCourse('Unable to save course progress.');
            break;
          }
        }
      });
      return saveQueue;
    };

    window.API_1484_11 = {
      Initialize: () => 'true',
      Terminate: () => {
        flushDirty();
        return 'true';
      },
      GetValue: (element) => data[element] || '',
      SetValue: (element, value) => {
        const next = String(value);
        if (data[element] === next) {
          return 'true';
        }
        data[element] = next;
        if (saved[element] === next) {
          dirty.delete(element);
        } else {
          dirty.add(element);
        }
        return 'true';
      },
      Commit: () => {
        flushDirty();
        return 'true';
      },
      GetLastError: () => '0',
      GetErrorString: () => 'No Error',
      GetDiagnostic: () => 'No Error',
    };

    (async () => {
      let out;
      try {
        out = await loadCourseState(course.code);
      } catch (err) {
        console.error('SCORM load failed', err);
        failCourse('Unable to load course progress.');
        return;
      }

      if (cancelled) {
        return;
      }

      if (!out || out.status !== 200) {
        failCourse(`Unable to load course progress. ${out?.message || ''}`.trim());
        return;
      }

      const state = out.state && typeof out.state === 'object' ? out.state : {};
      Object.entries(state).forEach(([key, value]) => {
        const stringValue = String(value);
        data[key] = stringValue;
        saved[key] = stringValue;
      });

      if (!iframeRef.current) {
        return;
      }
      iframeRef.current.src = scormSrc;
      window.practitionerPortalCourseStarted?.(course.code);
    })();

    return () => {
      cancelled = true;
      flushDirty();
      setChromeHidden(chrome, false);
      setPageScrollLocked(false);
      delete window.API_1484_11;
    };
  }, [scormSrc, course?.code, dispatch, sessionId]);

  if (!catalogueCourse || catalogueCourse.comingSoon) {
    return (
      <div>
        <PromoBox>
          <h1>Learning</h1>
        </PromoBox>
        <div className="container content">
          <div className="row">
            <div className="col-lg-12">
              <p>This course does not exist.</p>
              <p>
                <Link to="/learning">Back to Learning</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div>
        <PromoBox>
          <h1>Learning</h1>
        </PromoBox>
        <div className="container content">
          <div className="row">
            <div className="col-lg-12">
              <p>You do not have access to this course.</p>
              <p>
                <Link to="/learning">Back to Learning</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!scormSrc) {
    return (
      <div>
        <PromoBox>
          <h1>{course.name}</h1>
        </PromoBox>
        <div className="container content">
          <div className="row">
            <div className="col-lg-12">
              <p>SCORM course not found.</p>
              <p>
                <Link to="/learning">Back to Learning</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (courseError) {
    return (
      <div>
        <PromoBox>
          <h1>{course.name}</h1>
        </PromoBox>
        <div className="container content">
          <div className="row">
            <div className="col-lg-12">
              <p>{courseError}</p>
              <p>
                <Link to="/learning">Back to Learning</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      title={course.name}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        width: '100%',
        height: '100dvh',
        border: 0,
      }}
      allowFullScreen
    />
  );
};

export default LearningCourse;
